import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Package, CreditCard, Banknote } from 'lucide-react';
import { QrPayDialog } from './QrPayDialog';

const API = `${process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL}/api`;

export default function QuickCheckoutDialog({ 
  isOpen, 
  onClose, 
  appointment, 
  customer,
  onCheckInSuccess 
}) {
  const [activeTab, setActiveTab] = useState('single'); // Start with single price
  const [loading, setLoading] = useState(false);
  
  // Products
  const [packages, setPackages] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [service, setService] = useState(null);
  
  // Selection
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('bar');
  // QR/TWINT-Bezahlung direkt am Empfang (öffnet QR-Dialog nach dem Verkauf)
  const [qrInvoice, setQrInvoice] = useState(null);
  // Deferred TWINT/QR: geparkter Checkout (nichts gebucht bis bezahlt).
  const [qrDeferred, setQrDeferred] = useState(null);

  // Kreditkarten-Informationen für Direktbelastung
  const [hasSavedCard, setHasSavedCard] = useState(false);
  // Iter 351q: Zahlungsart „Karte vor Ort" — steuerbar in Einstellungen → Rechnungen
  const [cardOnSiteEnabled, setCardOnSiteEnabled] = useState(true);
  
  // Guthaben (CHF) und Punkte
  const [creditBalance, setCreditBalance] = useState(0);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [useCredit, setUseCredit] = useState(false);
  const [usePoints, setUsePoints] = useState(false);

  // interne Gruppierung: "rechnung_combo" wird als "rechnung" ans Backend geschickt
  // und "kreditkarte_direct" wird als "kreditkarte" ans Backend geschickt
  const normalizePaymentMethod = (value) => {
    if (value === 'rechnung_combo') return 'rechnung';
    if (value === 'kreditkarte_direct') return 'kreditkarte';
    return value;
  };
  
  // Single service discount
  const [discountType, setDiscountType] = useState('percentage');
  // Empty string statt 0 — sonst kann User die "0" nicht mit Backspace
  // entfernen und bekommt unschöne Eingaben wie "075". Beim Rechnen
  // wird mit `Number(discountValue) || 0` defensiv geparst.
  const [discountValue, setDiscountValue] = useState('');
  const [finalPrice, setFinalPrice] = useState(0);
  // 06/2026: Treue-Rabatt (25% aufs 2. Training/Woche) — Vorschau für den Trainer
  const [weeklyReward, setWeeklyReward] = useState(null);

  useEffect(() => {
    if (isOpen) {
      console.log('QuickCheckoutDialog opened!', { appointment, customer });
      fetchProducts();
      if (appointment?.service_id) {
        fetchService(appointment.service_id);
      }
      fetchWeeklyReward();
    } else {
      setWeeklyReward(null);
    }
  }, [isOpen, appointment]);

  useEffect(() => {
    if (service) {
      calculateFinalPrice();
    }
  }, [service, discountType, discountValue, weeklyReward]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      
      console.log('Fetching products...', API);
      
      // Fetch packages
      const packagesRes = await axios.get(`${API}/point-packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Packages fetched:', packagesRes.data);
      // Show all active packages
      setPackages(packagesRes.data.filter(p => p.is_active === true));
      
      // Fetch subscriptions
      const subsRes = await axios.get(`${API}/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Subscriptions fetched:', subsRes.data);
      // Show only templates (no customer_id) that are active and purchasable
      // Prüfen, ob der Kunde eine autorisierte Kreditkarte hinterlegt hat
      // + Iter 351q: ist „Karte vor Ort" in den Rechnungs-Einstellungen aktiv?
      try {
        const token = localStorage.getItem('token');
        const invRes = await axios.get(`${API}/invoice-settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCardOnSiteEnabled(invRes.data?.enable_card_on_site !== false);
      } catch (err) {
        setCardOnSiteEnabled(true);
      }
      if (customer?.id) {
        try {
          const token = localStorage.getItem('token');
          const tokensRes = await axios.get(`${API}/payrexx/customers/${customer.id}/payment-tokens`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const authorizedTokens = (tokensRes.data || []).filter((t) => t.status === 'authorized');
          const has = authorizedTokens.length > 0;
          setHasSavedCard(has);
          // Auto-select "Direkt belasten" als Default wenn Karte hinterlegt
          // ist — der Studio-Owner muss damit nicht jedes Mal manuell von
          // "Bar" umstellen. User kann jederzeit auf Bar/Rechnung wechseln.
          if (has) {
            setPaymentMethod('kreditkarte');
          } else {
            // Keine Karte hinterlegt → Standard: Rechnung / TWINT senden
            setPaymentMethod('rechnung_combo');
          }
        } catch (err) {
          console.error('Error fetching payment tokens:', err);
          setHasSavedCard(false);
          setPaymentMethod('rechnung_combo');
        }
        
        // Lade Kundendaten für Guthaben und Punkte
        try {
          const token = localStorage.getItem('token');
          const customerRes = await axios.get(`${API}/customers/by-user/${customer.user_id || customer.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCreditBalance(customerRes.data.credit_balance || 0);
          setPointsBalance(customerRes.data.points || 0);
          console.log('Customer balances:', { credit: customerRes.data.credit_balance, points: customerRes.data.points });
        } catch (err) {
          console.error('Error fetching customer balances:', err);
          setCreditBalance(0);
          setPointsBalance(0);
        }
      } else {
        setHasSavedCard(false);
        setCreditBalance(0);
        setPointsBalance(0);
      }

      // Lade allgemeine Subscriptions (zum Kaufen)
      const generalSubs = subsRes.data.filter(s => !s.customer_id && s.is_active === true);
      
      // Lade kundenspezifische recurring subscriptions
      let customerRecurringSubs = [];
      if (customer?.id) {
        try {
          const recurringRes = await axios.get(`${API}/customers/${customer.id}/recurring-subscriptions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('🔍 Recurring Subs API Response:', recurringRes.data);
          
          // Filter nur aktive Abos mit remaining_contingent > 0
          customerRecurringSubs = recurringRes.data.filter(s => {
            console.log(`🔍 Checking sub: ${s.name}, status: ${s.status}, remaining: ${s.remaining_contingent}`);
            return s.status === 'active' && s.remaining_contingent > 0;
          });
          console.log('✅ Filtered recurring subs:', customerRecurringSubs);
        } catch (err) {
          console.error('❌ Error fetching recurring subscriptions:', err);
        }
      }
      
      // Kombiniere beide Listen
      console.log('📋 Final subscriptions list:', [...generalSubs, ...customerRecurringSubs]);
      setSubscriptions([...generalSubs, ...customerRecurringSubs]);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Fehler beim Laden der Produkte');
    }
  };

  const fetchService = async (serviceId) => {    try {
      const token = localStorage.getItem('token');
      console.log('Fetching service:', serviceId);
      const res = await axios.get(`${API}/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Service fetched:', res.data);
      setService(res.data);
      
      // Check if customer has individual price for this service
      let priceToUse = res.data.price || 0;
      if (customer?.individual_prices && customer.individual_prices[serviceId]) {
        priceToUse = customer.individual_prices[serviceId];
        console.log('Using individual price for customer:', priceToUse);
      }
      
      setFinalPrice(priceToUse);
    } catch (error) {
      console.error('Error fetching service:', error);
      toast.error('Fehler beim Laden des Services');
    }
  };

  const fetchWeeklyReward = async () => {
    if (!appointment?.id) { setWeeklyReward(null); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/loyalty/weekly-reward-preview`, {
        params: { appointment_id: appointment.id, customer_id: customer?.id },
        headers: { Authorization: `Bearer ${token}` },
      });
      setWeeklyReward(res.data?.applies ? res.data : null);
    } catch (err) {
      setWeeklyReward(null);
    }
  };

  const calculateFinalPrice = () => {
    if (!service) return;
    
    // Use individual price if available, otherwise use service price
    let price = service.price || 0;
    if (customer?.individual_prices && customer.individual_prices[service.id]) {
      price = customer.individual_prices[service.id];
      console.log('Calculating with individual price:', price);
    }
    
    // Treue-Rabatt spiegeln (Backend wendet ihn ebenfalls an, „günstiger gewinnt")
    if (weeklyReward?.applies && typeof weeklyReward.reward_price === 'number') {
      price = Math.min(price, weeklyReward.reward_price);
    }

    const numericDiscount = Number(discountValue) || 0;
    if (numericDiscount > 0) {
      if (discountType === 'percentage') {
        price = price * (1 - numericDiscount / 100);
      } else {
        price = Math.max(0, price - numericDiscount);
      }
    }
    
    setFinalPrice(price);
  };

  const handleQuickCheckout = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // NEUE LOGIK: Wenn Kunde ein passendes Abo hat, verwende direkt Check-In Route!
      const matchingSub = subscriptions.find(sub => {
        if (!sub.hasOwnProperty('remaining_contingent')) return false;
        if (sub.remaining_contingent <= 0) return false;
        if (sub.status !== 'active') return false;
        
        // Prüfe Service-Einschränkungen
        const allowedServices = sub.allowed_service_ids;
        if (allowedServices && allowedServices.length > 0) {
          // Wenn Einschränkungen existieren, muss Service erlaubt sein
          const currentServiceId = service?.id;
          if (!currentServiceId) return false;
          return allowedServices.includes(currentServiceId);
        }
        
        // Keine Einschränkungen = alle Services erlaubt
        return true;
      });
      
      console.log('🔍 Matching sub check:', {
        subscriptions: subscriptions.length,
        matchingSub: matchingSub?.name || 'none',
        serviceId: service?.id
      });
      
      if (matchingSub) {
        console.log('✅ Matching subscription found, using direct check-in:', matchingSub.name);
        
        // Rufe direkt die Check-In Route auf (die automatisch vom Abo abzieht)
        // FLEX-Fix: customer_id als Query-Param, damit nur DIESER Teilnehmer eingecheckt wird
        await axios.post(
          `${API}/appointments/${appointment.id}/check-in?customer_id=${encodeURIComponent(customer.id)}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success(`✅ Check-In erfolgreich vom Abo: ${matchingSub.name}`);
        if (onCheckInSuccess) onCheckInSuccess();
        onClose();
        return;
      }
      
      // Fallback: Normale Checkout-Logik (wenn kein Abo)
      let productType, productId, discount = {};
      
      if (activeTab === 'packages') {
        if (!selectedPackage) {
          toast.error('Bitte wählen Sie ein Punkt-Paket aus');
          return;
        }
        productType = 'package';
        productId = selectedPackage;
      } else if (activeTab === 'subscriptions') {
        if (!selectedSubscription) {
          toast.error('Bitte wählen Sie ein Abo aus');
          return;
        }
        productType = 'subscription';
        productId = selectedSubscription;
      } else {
        productType = 'single_service';
        productId = null;
        if (Number(discountValue) > 0) {
          discount = {
            discount_type: discountType,
            discount_value: Number(discountValue)
          };
        }
      }
      
      const isQr = paymentMethod === 'twint_qr';
      const payload = {
        appointment_id: appointment.id,
        customer_id: customer.id,
        product_type: productType,
        product_id: productId,
        payment_method: isQr ? 'twint' : paymentMethod,
        use_credit: activeTab === 'single' && useCredit && creditBalance > 0,
        use_points: activeTab === 'single' && usePoints && pointsBalance >= (service?.points || 0),
        ...discount
      };

      // TWINT/QR: DEFERRED — es wird NICHTS gebucht, bevor der Kunde bezahlt hat.
      // Rechnung/Punkte/Guthaben/Check-in erfolgen erst nach bestätigter Zahlung
      // (Payrexx-Webhook). Der zurückgegebene `amount` ist bereits der Restbetrag
      // NACH Guthaben/Punkten (kein Doppelbelasten mehr).
      if (isQr) {
        const deferRes = await axios.post(
          `${API}/appointments/${appointment.id}/quick-checkout-defer?channel=qr`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Komplett durch Guthaben/Punkte/Abo gedeckt → sofort erledigt, kein QR.
        if (deferRes.data?.requires_payment !== true) {
          toast.success(deferRes.data?.message || 'Verkauf und Check-In erfolgreich!');
          if (onCheckInSuccess) onCheckInSuccess();
          onClose();
          return;
        }
        setQrDeferred({
          purchaseRef: deferRes.data.purchase_ref,
          paymentLink: deferRes.data.payment_link,
          amount: Number(deferRes.data.amount || 0),
        });
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API}/appointments/${appointment.id}/quick-checkout`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message || 'Verkauf und Check-In erfolgreich!');

      // Popup für Zahlungslink vorerst deaktiviert: Link in "Neue Rechnung"-Email enthalten
      // if (paymentMethod === 'rechnung_combo' || paymentMethod === 'rechnung') {
      //   const sendLink = window.confirm('Möchten Sie dem Kunden einen Zahlungslink per E-Mail/WhatsApp senden?');
      //   if (sendLink) {
      //     try {
      //       const channels = { email: true, whatsapp: true };
      //       await axios.post(
      //         `${API}/invoices/${response.data.invoice_id || ''}/send-payment-link`,
      //         channels,
      //         { headers: { Authorization: `Bearer ${token}` } }
      //       );
      //       toast.success('Zahlungslink an Kunde gesendet (Email/WhatsApp).');
      //     } catch (err) {
      //       console.error('Fehler beim Senden des Zahlungslinks:', err);
      //       toast.error(err.response?.data?.detail || 'Fehler beim Senden des Zahlungslinks');
      //     }
      //   }
      // }

      onCheckInSuccess();
      onClose();
    } catch (error) {
      console.error('Quick checkout error:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Verkauf');
    } finally {
      setLoading(false);
    }
  };

  const calculatePriceWithTax = (price, taxRate, taxIncluded) => {
    if (taxIncluded) {
      return price;
    } else {
      return price * (1 + taxRate / 100);
    }
  };

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Verkaufen & Check-In
          </DialogTitle>
          <div className="text-sm text-gray-600 mt-2">
            <p><strong>Kunde:</strong> {customer?.first_name} {customer?.last_name}</p>
            <p><strong>Service:</strong> {service?.name}</p>
          </div>
          
          {/* Kunden-Guthaben Anzeige */}
          <div className="flex gap-3 mt-3">
            {creditBalance > 0 && (
              <div className="bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
                <span className="text-xs text-green-600">💰 CHF-Guthaben</span>
                <div className="text-lg font-bold text-green-700">CHF {creditBalance.toFixed(2)}</div>
              </div>
            )}
            {pointsBalance > 0 && (
              <div className="bg-blue-50 border border-blue-200 px-3 py-2 rounded-lg">
                <span className="text-xs text-blue-600">🎯 Punkte</span>
                <div className="text-lg font-bold text-blue-700">{pointsBalance} Pkt</div>
              </div>
            )}
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="single">
              <Banknote className="w-4 h-4 mr-2" />
              Einzelpreis
            </TabsTrigger>
            <TabsTrigger value="packages">
              <Package className="w-4 h-4 mr-2" />
              Punkt-Pakete
            </TabsTrigger>
            <TabsTrigger value="subscriptions">
              <CreditCard className="w-4 h-4 mr-2" />
              Abos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="packages" className="space-y-4 mt-4">
            <div className="grid gap-3">
              {packages.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Keine Punkt-Pakete verfügbar</p>
              ) : (
                packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      selectedPackage === pkg.id
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedPackage(pkg.id)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-semibold">{pkg.name}</h4>
                        <p className="text-sm text-gray-600">{pkg.description}</p>
                        <p className="text-lg font-bold text-emerald-600 mt-1">
                          {pkg.points_amount} Punkte
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold">
                          CHF {calculatePriceWithTax(pkg.price, pkg.tax_rate || 8.1, pkg.tax_included).toFixed(2)}
                        </p>
                        {pkg.tax_included && (
                          <p className="text-xs text-gray-500">inkl. {pkg.tax_rate || 8.1}% MWST</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="subscriptions" className="space-y-4 mt-4">
            <div className="grid gap-3">
              {subscriptions.length === 0 && (
                <p className="text-gray-500 text-center py-8">Keine Abos verfügbar</p>
              )}
              {subscriptions.length > 0 && (
                subscriptions.map((sub) => {
                  // Unterscheide zwischen recurring subscriptions und normalen subscriptions
                  const isRecurring = sub.hasOwnProperty('remaining_contingent');
                  
                  console.log(`🔍 Rendering sub: ${sub.name}, isRecurring: ${isRecurring}`);
                  
                  // Check if has credits
                  let hasCredits = false;
                  if (isRecurring) {
                    // Recurring subscription: check remaining_contingent
                    hasCredits = (sub.remaining_contingent !== null && sub.remaining_contingent !== undefined && sub.remaining_contingent > 0);
                    console.log(`  → Recurring: remaining_contingent=${sub.remaining_contingent}, hasCredits=${hasCredits}`);
                  } else {
                    // Normal subscription: check credits_remaining OR credits_total (for new subs)
                    hasCredits = (sub.credits_remaining > 0) || (sub.credits_total > 0 && !sub.customer_id);
                    console.log(`  → Normal: credits_remaining=${sub.credits_remaining}, credits_total=${sub.credits_total}, hasCredits=${hasCredits}`);
                  }
                  
                  if (!hasCredits) {
                    console.log(`  ❌ Skipping ${sub.name} - no credits`);
                    return null; // Skip wenn keine Credits
                  }
                  
                  console.log(`  ✅ Rendering ${sub.name}`);
                  
                  return (
                    <div
                      key={sub.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${
                        selectedSubscription === sub.id
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedSubscription(sub.id)}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <h4 className="font-semibold">{sub.name}</h4>
                          <p className="text-sm text-gray-600">{sub.description || (isRecurring ? 'Monats-Abo' : '')}</p>
                          <div className="flex gap-2 mt-2">
                            {isRecurring ? (
                              <>
                                <Badge className="bg-blue-500">Verbleibend: {sub.remaining_contingent} Punkte</Badge>
                                {sub.renewal_mode === 'on_depletion' && <Badge variant="outline">🔄 Auto-Reload</Badge>}
                              </>
                            ) : (
                              <>
                                <Badge>
                                  {sub.credits_remaining !== undefined 
                                    ? `${sub.credits_remaining} / ${sub.credits_total} Credits`
                                    : `${sub.credits_total} Credits`}
                                </Badge>
                                {sub.validity_days && <Badge variant="outline">{sub.validity_days} Tage gültig</Badge>}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {isRecurring ? (
                            <div className="text-emerald-600 font-bold">Vom Abo</div>
                          ) : (
                            <p className="text-2xl font-bold">CHF {(sub.price || 0).toFixed(2)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>

          <TabsContent value="single" className="space-y-4 mt-4">
            {service && (
              <div className="space-y-4">
                {weeklyReward?.applies && (
                  <div
                    data-testid="checkout-weekly-reward-badge"
                    className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 flex items-center gap-2"
                  >
                    <span className="text-xl">🎁</span>
                    <div className="text-sm">
                      <p className="font-semibold text-emerald-800">
                        {weeklyReward.percent}% Treue-Rabatt · {weeklyReward.session_index}. Training diese Woche
                      </p>
                      <p className="text-emerald-700">
                        statt CHF {(weeklyReward.standard_price || 0).toFixed(2)} nur CHF {(weeklyReward.reward_price || 0).toFixed(2)}
                        {weeklyReward.standard_points > 0
                          ? ` · ${weeklyReward.reward_points} statt ${weeklyReward.standard_points} Pkt`
                          : ''}
                      </p>
                    </div>
                  </div>
                )}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Service-Details</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Service:</div>
                    <div className="font-semibold">{service.name}</div>
                    <div>Dauer:</div>
                    <div>{service.duration_minutes} Min</div>
                    <div>Preis:</div>
                    <div className="font-semibold text-emerald-600">CHF {(service.price || 0).toFixed(2)}</div>
                  </div>
                </div>
                
                {/* Guthaben verwenden */}
                {(creditBalance > 0 || pointsBalance > 0) && (
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-semibold mb-3">💳 Guthaben verwenden</h4>
                    <div className="space-y-2">
                      {creditBalance > 0 && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={useCredit}
                            onChange={(e) => setUseCredit(e.target.checked)}
                            data-testid="quick-checkout-use-credit"
                            className="rounded border-green-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm">
                            💰 CHF-Guthaben verwenden 
                            <span className="font-bold text-green-700 ml-1">
                              (CHF {Math.min(creditBalance, finalPrice).toFixed(2)} verfügbar)
                            </span>
                          </span>
                        </label>
                      )}
                      {pointsBalance > 0 && service?.points > 0 && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={usePoints}
                            onChange={(e) => setUsePoints(e.target.checked)}
                            className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm">
                            🎯 Punkte verwenden 
                            <span className="font-bold text-blue-700 ml-1">
                              ({pointsBalance} Pkt verfügbar, Service: {service.points} Pkt)
                            </span>
                          </span>
                        </label>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <Label>Rabatt</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={discountType} onValueChange={setDiscountType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Prozent (%)</SelectItem>
                        <SelectItem value="fixed">Fester Betrag (CHF)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      placeholder={discountType === 'percentage' ? '0%' : '0 CHF'}
                    />
                  </div>
                </div>

                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span>Service-Preis:</span>
                      <span>CHF {(service?.price || 0).toFixed(2)}</span>
                    </div>
                    {weeklyReward?.applies && (
                      <div className="flex justify-between items-center text-sm text-emerald-700">
                        <span>🎁 Treue-Rabatt (2. Training) −{weeklyReward.percent}%:</span>
                        <span>- CHF {((weeklyReward.standard_price || 0) - (weeklyReward.reward_price || 0)).toFixed(2)}</span>
                      </div>
                    )}
                    {Number(discountValue) > 0 && (
                      <div className="flex justify-between items-center text-sm text-orange-600">
                        <span>Rabatt:</span>
                        <span>- CHF {(discountType === 'percentage' ? (service?.price || 0) * Number(discountValue) / 100 : Number(discountValue)).toFixed(2)}</span>
                      </div>
                    )}
                    {useCredit && creditBalance > 0 && (
                      <div className="flex justify-between items-center text-sm text-green-600">
                        <span>💰 CHF-Guthaben:</span>
                        <span>- CHF {Math.min(creditBalance, finalPrice).toFixed(2)}</span>
                      </div>
                    )}
                    {usePoints && pointsBalance >= (service?.points || 0) && (
                      <div className="flex justify-between items-center text-sm text-blue-600">
                        <span>🎯 Punkte ({service?.points || 0} Pkt):</span>
                        <span>- CHF {finalPrice.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex justify-between items-center">
                      <span className="font-semibold">Zu zahlen:</span>
                      <span className="text-2xl font-bold text-emerald-600">
                        CHF {Math.max(0, 
                          usePoints && pointsBalance >= (service?.points || 0) ? 0 :
                          finalPrice - (useCredit ? Math.min(creditBalance, finalPrice) : 0)
                        ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Zahlungsart - nur anzeigen wenn noch etwas zu zahlen ist */}
        {(() => {
          const priceAfterCredit = usePoints && pointsBalance >= (service?.points || 0)
            ? 0
            : finalPrice - (useCredit ? Math.min(creditBalance, finalPrice) : 0);

          if (priceAfterCredit <= 0) {
            // Genaue Begründung anzeigen — vorher stand IMMER "Gedeckt durch
            // CHF-Guthaben" auch wenn der Kunde gar kein Guthaben hatte und
            // der Preis nur durch einen 100%-Rabatt auf 0 fiel. Das war
            // verwirrend für den Studio-Owner ("Wieso hat der Kunde Guthaben?").
            //
            // Reihenfolge der Checks: spezifischste Begründung zuerst.
            const originalServicePrice = Number(service?.price) || 0;
            const numericDiscount = Number(discountValue) || 0;
            let reason;
            if (usePoints && pointsBalance >= (service?.points || 0)) {
              reason = 'Bezahlt mit Punkten';
            } else if (useCredit && creditBalance > 0 && Math.min(creditBalance, finalPrice) > 0) {
              reason = `Gedeckt durch CHF-Guthaben (CHF ${Math.min(creditBalance, finalPrice).toFixed(2)})`;
            } else if (originalServicePrice === 0 && numericDiscount === 0) {
              // Service-Preis ist von Haus aus 0 (Gratis-Service, im Abo
              // enthaltener Termin, etc.) — kein Rabatt im Spiel.
              reason = 'Service-Preis ist CHF 0.00 (Gratis-Service)';
            } else if (numericDiscount >= 100 && discountType === 'percentage') {
              reason = '100% Rabatt';
            } else if (numericDiscount > 0 && discountType === 'percentage') {
              reason = `Preis durch ${numericDiscount}% Rabatt auf CHF 0.00 reduziert`;
            } else if (numericDiscount > 0 && discountType === 'fixed') {
              reason = `Preis durch CHF ${numericDiscount.toFixed(2)} Rabatt auf CHF 0.00 reduziert`;
            } else {
              reason = 'Kein Betrag zu zahlen';
            }
            return (
              <div className="bg-green-100 border border-green-300 p-4 rounded-lg mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🎁</span>
                  <div>
                    <p className="font-semibold text-green-800">Gratiskauf</p>
                    <p className="text-sm text-green-600">{reason}</p>
                  </div>
                </div>
              </div>
            );
          }
          
          return (
            <div className="space-y-3 mt-4">
              <Label>Zahlungsart</Label>
              <Select
                value={paymentMethod === 'rechnung' ? 'rechnung_combo' : paymentMethod === 'kreditkarte' ? 'kreditkarte_direct' : paymentMethod}
                onValueChange={(val) => setPaymentMethod(normalizePaymentMethod(val))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bar">💵 Bar</SelectItem>
                  {/* Iter 351q: Kartenzahlung am Terminal im Studio — wird
                      wie Bar sofort als bezahlt verbucht. Ein-/ausschaltbar
                      in Einstellungen → Rechnungen. */}
                  {cardOnSiteEnabled && (
                    <SelectItem value="karte_vor_ort" data-testid="payment-method-karte-vor-ort">💳 Karte (vor Ort)</SelectItem>
                  )}
                  <SelectItem value="rechnung_combo">📄 Rechnung / TWINT senden</SelectItem>
                  <SelectItem value="twint_qr" data-testid="payment-method-twint-qr">📱 TWINT / QR (Kunde scannt vor Ort)</SelectItem>
                  {/* Direkt belasten — verfügbar SOBALD eine Karte hinterlegt
                      ist. Vorher waren zusätzlich `payment_timing===sofort`
                      und `auto_charge_card===true` nötig — das schloss aber
                      Kunden aus, die nur eine Karte für gelegentliche Pay-on-
                      demand Buchungen hinterlegt hatten. */}
                  {hasSavedCard && (
                    <SelectItem value="kreditkarte_direct">💳 Direkt belasten (hinterlegte Karte)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          );
        })()}

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Abbrechen
          </Button>
          <Button onClick={handleQuickCheckout} disabled={loading} data-testid="quick-checkout-submit" className="bg-emerald-600 hover:bg-emerald-700">
            {loading ? 'Wird verarbeitet...' : 'Verkaufen & Check-In'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <QrPayDialog
      invoice={qrInvoice}
      open={!!qrInvoice}
      onOpenChange={(o) => { if (!o) { setQrInvoice(null); onClose(); } }}
      onPaid={() => { setQrInvoice(null); if (onCheckInSuccess) onCheckInSuccess(); onClose(); }}
    />

    <QrPayDialog
      deferred={qrDeferred}
      open={!!qrDeferred}
      onOpenChange={(o) => { if (!o) { setQrDeferred(null); onClose(); } }}
      onPaid={() => { setQrDeferred(null); if (onCheckInSuccess) onCheckInSuccess(); onClose(); }}
    />
    </>
  );
}
